import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import "tldraw/tldraw.css";
import useLiveSessionFirestore from "../../../hooks/useLiveSessionFirestore";
import { getDeviceId } from "../../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { pushModal } from "../../../store/slices/modalsSlice";
import GeneralMessageModal from "../../Modals/GeneralMessageModal";
import PurchaseScreen from "../../PurchaseScreen";
import SpinnerScreen from "../../SpinnerScreen";
import useCheckIsMobile from "../lessonRefactor/utils/useCheckIsMobile";
import Chat from "./Chat";
import CountdownTimer from "./CountdownTimer";
import useGetLiveSession from "./hooks/useGetLiveSession";
import useLessonStartStatus from "./hooks/useLessonStartStatus";
import useStreamIO from "./hooks/useStreamIO";
import LiveSessionTimer from "./LiveSessionTimer";
import QuestionDisplay from "./QuestionDisplay";
import SessionCanvas from "./SessionCanvas";
import StudentToolbar from "./StudentToolbar";
import styles from "./TutorLiveScreen.module.css";

const StudentLiveScreen = () => {
  const deviceId = getDeviceId();
  const { schoolgrade, group } = useParams();
  const sessionData = useGetLiveSession();
  const { isStarted, loading: startStatusLoading } = useLessonStartStatus(
    sessionData?.lesson_id
  );
  const { isMobile } = useCheckIsMobile();
  const { uid, user_display_name } = useAppSelector(
    (state) => state.store.user
  );
  const { active_mics, hands_raised, kickedOut, question, studentsDevices } =
    useAppSelector((state) => state.liveSession);

  console.log("vascfeafga", studentsDevices);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const {
    isMuted,
    isMicLoading,
    isRTCStarted,
    joinCall,
    leaveCall,
    setMicMuted,
  } = useStreamIO({
    uid: uid?.toString(),
    token: sessionData?.join_token,
    callId: sessionData?.lesson_id.toString(),
  });

  const {
    addStudent,
    removeStudent,
    raiseHand,
    lowerHand,
    sendMessage,
    openMic,
    closeMic,
    addStudentAnswer,
  } = useLiveSessionFirestore(
    sessionData?.lesson_id.toString(),
    sessionData?.is_purchased,
    sessionData?.current_server_time
  );

  const exitCall = useCallback(async () => {
    await leaveCall();
    await removeStudent(uid);
    navigate(`/live`);
  }, [leaveCall, removeStudent, uid]);

  const joinCallAsStudent = useCallback(async () => {
    await joinCall();
    await addStudent(uid, user_display_name);
  }, [joinCall, addStudent, uid, user_display_name]);

  const toggleMic = useCallback(async () => {
    if (active_mics?.includes(uid)) {
      await closeMic(uid);
    } else {
      await openMic(uid);
    }
  }, [openMic, closeMic, uid, active_mics]);

  const toggleHandRaise = useCallback(async () => {
    if (hands_raised?.includes(uid)) {
      await lowerHand(uid);
    } else {
      await raiseHand(uid);
    }
  }, [lowerHand, raiseHand, uid, hands_raised]);

  const kickoutOldSession = useCallback(async () => {
    dispatch(
      pushModal(
        <GeneralMessageModal
          msg="لا يمكنك دخول الحصة من جهازين"
          btnMsg="حسناً"
        />
      )
    );
    await exitCall();
  }, [exitCall]);

  useEffect(() => {
    if (uid && sessionData?.is_purchased) {
      addStudent(uid, user_display_name);
      return () => {
        removeStudent(uid);
      };
    }
  }, [uid, user_display_name, addStudent, removeStudent, sessionData]);

  useEffect(() => {
    if (kickedOut?.includes(uid)) {
      dispatch(
        pushModal(
          <GeneralMessageModal msg="لقد أخرجك المدرس من الحصة" btnMsg="حسناً" />
        )
      );
      exitCall();
    }
  }, [uid, kickedOut]);

  useEffect(() => {
    if (active_mics?.includes(uid)) {
      setMicMuted(true);
    } else {
      setMicMuted(false);
    }
  }, [uid, active_mics]);

  useEffect(() => {
    if (!sessionData?.is_purchased || studentsDevices?.length < 2) return;

    const currentStudentSessions = studentsDevices?.filter(
      (student) => student.studentId == uid
    );

    if (currentStudentSessions?.length > 1) {
      const localStudentSessions = currentStudentSessions?.filter(
        (student) => student.deviceId == deviceId
      );
      const remoteStudentSessions = currentStudentSessions?.filter(
        (student) => student.deviceId != deviceId
      );

      const latestLocalSession = localStudentSessions?.sort(
        (a, b) => b.serverTimestamp - a.serverTimestamp
      )[0];

      const latestRemoteSession = remoteStudentSessions?.sort(
        (a, b) => b.serverTimestamp - a.serverTimestamp
      )[0];

      if (
        latestLocalSession?.serverTimestamp <
        latestRemoteSession?.serverTimestamp
      )
        kickoutOldSession();
    }
  }, [studentsDevices]);

  if (!sessionData || startStatusLoading) return <SpinnerScreen />;

  if (!sessionData.is_purchased)
    return <PurchaseScreen lessonData={sessionData} isMobile={isMobile} />;

  if (!isStarted)
    return <CountdownTimer targetDate={sessionData.session_start_date} />;

  return (
    <div className="fade-in m-0 p-0 w-full h-screen bg-black flex flex-col grow justify-center items-center">
      <div id="tldraw-live-session" className={styles.container}>
        <div className={styles.header}>
          <StudentToolbar
            isStarted={isStarted}
            isMuted={isMuted}
            isMicLoading={isMicLoading}
            isRTCStarted={isRTCStarted}
            joinCall={joinCallAsStudent}
            toggleMic={toggleMic}
            toggleHandRaise={toggleHandRaise}
          />
          <span className={styles.title}>{sessionData.title}</span>
          <LiveSessionTimer
            startDate={sessionData.started_at}
            sessionEnded={sessionData?.is_ended}
            sessionDuration={sessionData?.session_duration}
          />
          <button className={styles.closeBtn}>
            <img
              src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/exit-icon.svg"
              alt="انهاء الحصة"
              onClick={exitCall}
            />
          </button>
        </div>
        <div className={styles.body}>
          <div className={styles.sidebar}>
            <div className={styles.tabContent}>
              {question ? (
                <QuestionDisplay addStudentAnswer={addStudentAnswer} />
              ) : (
                <Chat sendMessage={sendMessage} />
              )}
            </div>
          </div>
          <div className={styles.sessionContent}>
            <SessionCanvas sessionId={sessionData?.lesson_id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLiveScreen;
